import { css } from 'lit';

export const contactsStyles = css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-left {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      /* ─── Search & Filter Bar ─── */
      .filter-bar {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-8);
        flex-wrap: wrap;
      }

      .search-input-wrap {
        position: relative;
        flex: 1;
        min-width: 240px;
        max-width: 400px;
      }

      .search-input-wrap .input {
        padding-left: var(--space-10);
      }

      .search-input-wrap .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
        pointer-events: none;
      }

      .filter-chips {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .filter-chip {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .filter-chip:hover {
        border-color: var(--color-border-secondary);
        background: var(--color-bg-tertiary);
      }

      .filter-chip.active {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .sort-select {
        padding: var(--space-2) var(--space-3);
        padding-right: var(--space-8);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        background: var(--color-bg-secondary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2357534E' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
        transition: all var(--transition-fast);
      }

      .sort-select:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      /* ─── Contact Table ─── */
      .table-container {
        border-radius: var(--radius-xl);
        border: 1px solid rgba(255, 255, 255, 0.35);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-full);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-display);
        font-size: var(--text-xs);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      }

      .name-cell {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .name-text {
        font-family: var(--font-display);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .phone-mono {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
      }

      .tag-chips {
        display: flex;
        gap: var(--space-1);
        flex-wrap: wrap;
      }

      .tag-chip {
        display: inline-flex;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: 0.625rem;
        font-weight: var(--font-semibold);
        letter-spacing: var(--tracking-wide);
      }

      .tag-vip { background: var(--color-primary-subtle); color: var(--color-primary); }
      .tag-new { background: var(--color-accent-subtle); color: var(--color-accent); }
      .tag-regular { background: var(--color-success-bg); color: var(--color-success-text); }
      .tag-lead { background: var(--color-warning-bg); color: var(--color-warning-text); }
      .tag-inactive { background: var(--color-bg-tertiary); color: var(--color-text-tertiary); }
      .tag-followup { background: var(--color-info-bg); color: var(--color-info-text); }

      .sentiment-dots {
        display: flex;
        align-items: center;
        gap: 3px;
      }

      .sentiment-dot {
        width: 8px;
        height: 8px;
        border-radius: var(--radius-full);
      }

      .sentiment-dot.positive { background: var(--color-success); }
      .sentiment-dot.negative { background: var(--color-error); }
      .sentiment-dot.neutral { background: var(--color-text-disabled); }

      .relative-time {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .row-action-btn {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .row-action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .row-actions {
        display: flex;
        gap: var(--space-1);
      }

      .total-calls-cell {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
      }

      /* ─── Detail View / Slide Over ─── */
      .detail-view {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        background: none;
        border: none;
        cursor: pointer;
        padding: var(--space-1) 0;
        margin-bottom: var(--space-5);
        transition: color var(--transition-fast);
      }

      .back-btn:hover {
        color: var(--color-text-primary);
      }

      /* Detail Header */
      .contact-detail-header {
        display: flex;
        align-items: center;
        gap: var(--space-5);
        margin-bottom: var(--space-6);
        padding-bottom: var(--space-6);
        border-bottom: 1px solid var(--color-border-primary);
      }

      .detail-avatar {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      }

      .detail-header-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .detail-name {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-meta {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }

      .detail-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .detail-meta-item.mono {
        font-family: var(--font-mono);
      }

      /* Detail Sections */
      .detail-sections {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
      }

      .detail-section-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-4);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .detail-section-title i {
        color: var(--color-text-tertiary);
      }

      /* Info Section */
      .info-tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }

      .info-tag {
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
      }

      .notes-field {
        width: 100%;
        min-height: 80px;
        padding: var(--space-3);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        background: var(--color-bg-tertiary);
        resize: vertical;
        line-height: var(--leading-relaxed);
      }

      .notes-field:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
        background: var(--color-bg-secondary);
      }

      .custom-fields {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
        margin-top: var(--space-4);
      }

      .custom-field {
        display: flex;
        flex-direction: column;
        gap: var(--space-0\.5);
      }

      .custom-field-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        font-weight: var(--font-medium);
      }

      .custom-field-value {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        font-weight: var(--font-medium);
      }

      /* Call History */
      .call-history-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .call-history-item {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        background: var(--color-bg-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .call-history-item:hover {
        background: var(--color-bg-hover);
      }

      .call-date {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        min-width: 100px;
      }

      .call-duration {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        min-width: 50px;
      }

      .call-sentiment-icon {
        font-size: var(--text-sm);
      }

      .call-sentiment-icon.positive { color: var(--color-success); }
      .call-sentiment-icon.negative { color: var(--color-error); }
      .call-sentiment-icon.neutral { color: var(--color-text-tertiary); }

      .call-expand-icon {
        margin-left: auto;
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        transition: transform var(--transition-fast);
      }

      .call-expand-icon.open {
        transform: rotate(180deg);
      }

      .call-transcript-summary {
        padding: var(--space-3) var(--space-4);
        padding-left: calc(var(--space-4) + 100px + var(--space-4));
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        line-height: var(--leading-relaxed);
        background: var(--color-bg-secondary);
        border-radius: 0 0 var(--radius-md) var(--radius-md);
        border: 1px solid var(--color-border-subtle);
        border-top: none;
        animation: slideDown var(--duration-base) var(--ease-out) both;
      }

      /* AI Memory Section */
      .ai-memory-card {
        background: var(--color-info-light);
        border: 2px solid var(--color-info-bg);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: var(--shadow-sm);
      }

      .ai-memory-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }

      .ai-memory-header i {
        color: var(--color-info);
        font-size: var(--text-base);
      }

      .ai-memory-header span {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-info-text);
        letter-spacing: var(--tracking-tight);
      }

      .ai-memory-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .ai-memory-item {
        display: flex;
        gap: var(--space-3);
        align-items: flex-start;
      }

      .ai-memory-bullet {
        width: 6px;
        height: 6px;
        margin-top: 7px;
        border-radius: var(--radius-full);
        background: var(--color-info);
        flex-shrink: 0;
      }

      .ai-memory-text {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-info-text);
        line-height: var(--leading-relaxed);
      }

      /* ─── Detail View Card Overrides ─── */
      .detail-sections .card {
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
        padding: var(--space-6);
      }

      /* ─── Responsive ─── */
      @media (max-width: 920px) {
        .filter-bar {
          flex-direction: column;
          align-items: stretch;
        }
        .search-input-wrap {
          max-width: none;
        }
        .custom-fields {
          grid-template-columns: 1fr;
        }
      }
`;
